import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Archive,
  Badge,
  Button,
  Code02,
  copyText,
  DotsVertical,
  Edit01,
  EmptyState,
  Eye,
  Gift01,
  Link01,
  Menu,
  Modal,
  PageHeader,
  Plus,
  Table,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import CheckoutEmbedModal from "../../shared/CheckoutEmbedModal/CheckoutEmbedModal";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import {
  STATUS_ARCHIVED,
  STATUS_COLORS,
  STATUS_PUBLISHED,
  STATUS_LABELS,
  formatAmounts,
  formatCents,
  scopeLabel,
} from "../giftCardFormat";
import GiftCardTabs, { TAB_PRODUCTS } from "./GiftCardTabs";
import GiftCardSalesOffAlert, { giftCardSalesShape, turnOnFailedMessage, turnOnGiftCardSales } from "./GiftCardSalesOffAlert";
import "../styles/giftCardProducts.css";

const copyWithToast = async (text, label) => {
  if (await copyText(text)) {
    toastSuccess(`${label} copied`);
  } else {
    toastError(`Couldn't copy the ${label.toLowerCase()}`);
  }
};

const embedIframeId = (product) => `opendate-gift-card-${product.id}`;

const columns = (currency, actionsFor, canManage) => [
  {
    key: "name",
    header: "Product",
    primary: true,
    render: (product) =>
      canManage ? (
        <a className="gift-card-products-name" href={product.editPath}>
          {product.name}
        </a>
      ) : (
        <span className="gift-card-products-name">{product.name}</span>
      ),
  },
  { key: "amounts", header: "Amounts", render: (product) => formatAmounts(product, currency) },
  { key: "scope", header: "Works at", render: (product) => scopeLabel(product.scope, product.venueNames) },
  {
    key: "status",
    header: "Status",
    render: (product) => (
      <Badge color={STATUS_COLORS[product.status]} dot size="sm">
        {STATUS_LABELS[product.status]}
      </Badge>
    ),
  },
  {
    key: "sold",
    header: "Sold",
    align: "right",
    render: (product) => new Intl.NumberFormat("en-US").format(product.soldCount),
  },
  {
    key: "outstanding",
    header: "Outstanding",
    align: "right",
    render: (product) => formatCents(product.outstandingCents, currency),
  },
  {
    key: "actions",
    header: "",
    align: "right",
    render: (product) => {
      const items = actionsFor(product);
      if (items.length === 0) return null;
      return (
        <Menu
          placement="bottom-end"
          items={items}
          trigger={
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`Actions for ${product.name}`}
              data-testid={`gift-card-product-actions-${product.id}`}
            />
          }
        />
      );
    },
  },
];

const GiftCardProductsList = ({
  featureEnabled,
  canManage,
  currency,
  newProductPath,
  settingsPath,
  productsPath,
  cardsPath,
  redeemPath,
  products: initialProducts,
  giftCardSales,
}) => {
  const [products, setProducts] = useState(initialProducts);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [embedTarget, setEmbedTarget] = useState(null);
  const [salesEnabled, setSalesEnabled] = useState(giftCardSales.enabled);
  const [turningOnSales, setTurningOnSales] = useState(false);

  const turnOnSales = async () => {
    setTurningOnSales(true);
    try {
      await turnOnGiftCardSales(giftCardSales.settingsPath);
      setSalesEnabled(true);
      toastSuccess("Gift card sales are on.");
    } catch (requestError) {
      toastError(turnOnFailedMessage(requestError));
    } finally {
      setTurningOnSales(false);
    }
  };

  const goToSettings = () => {
    window.location.href = settingsPath;
  };

  const goToNewProduct = () => {
    window.location.href = newProductPath;
  };

  const archiveProduct = async () => {
    if (archiving) return;
    setArchiving(true);
    try {
      const { data } = await client.post(archiveTarget.archivePath);
      setProducts((current) =>
        current.map((product) => (product.id === archiveTarget.id ? { ...product, status: data.status } : product))
      );
      toastSuccess(`${archiveTarget.name} archived`);
      setArchiveTarget(null);
    } catch (error) {
      toastError("Something went wrong. Please try again.");
    } finally {
      setArchiving(false);
    }
  };

  const actionsFor = (product) => {
    const open = () => {
      window.location.href = product.editPath;
    };
    const shareItems =
      product.status === STATUS_PUBLISHED
        ? [
            { key: "copy-link", label: "Copy link", icon: Link01, onClick: () => copyWithToast(product.purchaseUrl, "Link") },
            { key: "copy-embed", label: "Embed code", icon: Code02, onClick: () => setEmbedTarget(product) },
          ]
        : [];
    if (!canManage) return shareItems;
    if (product.status === STATUS_ARCHIVED) return [{ key: "view", label: "View", icon: Eye, onClick: open }];

    return [{ key: "edit", label: "Edit", icon: Edit01, onClick: open }].concat(shareItems, [
      { divider: true },
      { key: "archive", label: "Archive", icon: Archive, destructive: true, onClick: () => setArchiveTarget(product) },
    ]);
  };

  const header = (
    <PageHeader
      title="Gift cards"
      supportingText="Sell gift cards fans can use on tickets and add-ons."
      actions={
        featureEnabled &&
        canManage && (
          <div className="gift-card-products-actions">
            <Button variant="secondary" size="sm" onClick={goToSettings}>
              Settings
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={goToNewProduct}>
              New product
            </Button>
          </div>
        )
      }
    />
  );

  if (!featureEnabled) {
    return (
      <div className="gift-card-products">
        {header}
        <div className="gift-card-products-empty" data-testid="gift-card-products-disabled">
          <EmptyState
            icon={Gift01}
            title="Gift cards aren't on for your team"
            description="Opendate support can turn them on. Your Gift Up cards keep working until you switch."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="gift-card-products">
      {header}
      <GiftCardTabs current={TAB_PRODUCTS} productsPath={productsPath} cardsPath={cardsPath} redeemPath={redeemPath} />
      {!salesEnabled && products.some((product) => product.status !== STATUS_ARCHIVED) && (
        <GiftCardSalesOffAlert
          sales={giftCardSales}
          canManage={canManage}
          turningOn={turningOnSales}
          onTurnOn={turnOnSales}
        />
      )}
      {products.length === 0 ? (
        <div className="gift-card-products-empty" data-testid="gift-card-products-empty">
          <EmptyState
            icon={Gift01}
            title="No gift cards yet"
            description={
              canManage
                ? "Create a gift card, then share its link or embed it on your site."
                : "An administrator can create gift cards for your team."
            }
            primaryAction={canManage ? { label: "Create your first gift card", icon: Plus, onClick: goToNewProduct } : undefined}
          />
        </div>
      ) : (
        <div className="gift-card-products-table">
          <Table columns={columns(currency, actionsFor, canManage)} data={products} getRowKey={(product) => product.id} />
        </div>
      )}
      {embedTarget && (
        <CheckoutEmbedModal
          open
          onClose={() => setEmbedTarget(null)}
          iframeId={embedIframeId(embedTarget)}
          title={embedTarget.name}
          embedUrl={embedTarget.purchaseUrl}
          heading="Embed gift card"
          intro="Add this snippet to your website so fans can buy this gift card."
          widgetInstructions="Gift card widget — paste where you want it to appear on your page."
          showTrafficTag={false}
        />
      )}
      <Modal
        open={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        type="error"
        title={archiveTarget ? `Archive ${archiveTarget.name}?` : ""}
        description="It comes off sale and its link stops selling cards. Cards already sold keep working."
        confirmLabel={archiving ? "Archiving…" : "Archive"}
        cancelLabel="Cancel"
        onConfirm={archiveProduct}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

GiftCardProductsList.propTypes = {
  featureEnabled: PropTypes.bool.isRequired,
  canManage: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  newProductPath: PropTypes.string.isRequired,
  settingsPath: PropTypes.string.isRequired,
  productsPath: PropTypes.string.isRequired,
  cardsPath: PropTypes.string.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      scope: PropTypes.string.isRequired,
      venueNames: PropTypes.arrayOf(PropTypes.string),
      denominationsCents: PropTypes.arrayOf(PropTypes.number),
      customMinCents: PropTypes.number,
      customMaxCents: PropTypes.number,
      soldCount: PropTypes.number.isRequired,
      outstandingCents: PropTypes.number.isRequired,
      purchaseUrl: PropTypes.string.isRequired,
      editPath: PropTypes.string.isRequired,
      archivePath: PropTypes.string.isRequired,
    })
  ).isRequired,
  redeemPath: PropTypes.string,
  giftCardSales: giftCardSalesShape.isRequired,
};

GiftCardProductsList.defaultProps = {
  redeemPath: null,
};

export default GiftCardProductsList;
